import { useEffect, useState } from 'react';
import { ReactNode } from 'react';
import Button from '@alltrails/shared/denali/components/Button';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { loadRecaptcha, executeRecaptcha } from 'utils/recaptcha_helpers';
import { validateUserForm } from 'utils/UserFormHelper';
import { track } from 'utils/OptimizelyHelpers';
import { defineMessages, FormattedMessage } from 'react-intl';
import Checkbox from 'components/forms/Checkbox';
import OutlinedTextField from 'components/forms/textFields/OutlinedTextField';
import * as formStyles from 'components/forms/styles/styles.module.scss';
import useEnableRecaptcha from 'hooks/useEnableRecaptcha';
import useCountryId from 'hooks/useCountryId';
import * as styles from './RegisterForm.module.scss';
import { emailSignupHandler } from '../../utils/AuthHandlers';

const messages = defineMessages({
  signUp: {
    defaultMessage: 'Sign up'
  }
});

type Props = {
  autoFocus?: boolean;
  referralLink?: string;
  handleRegistrationSuccess: any;
  userEmailSubscribed?: boolean;
};

export default function RegisterForm({ autoFocus = false, referralLink, handleRegistrationSuccess, userEmailSubscribed }: Props) {
  const {
    formattedDefaultMessages: { signUp }
  } = useFormatMessage(messages);

  const countryId = useCountryId();
  const enableRecaptcha = useEnableRecaptcha();

  const [disableSubmit, setDisableSubmit] = useState<boolean>(enableRecaptcha);
  const [recaptchaObject, setRecaptchaObject] = useState<any>();
  const [serverError, setServerError] = useState();

  const [state, setState] = useState<{
    userEmailSubscribed: boolean;
    userRegLastName: string;
    userRegEmail: string;
    userRegPassword: string;
    userRegFirstName: string;
    submitting: boolean;
    showErrorsOnType: boolean;
  }>({
    userRegEmail: '',
    userRegPassword: '',
    userRegFirstName: '',
    userRegLastName: '',
    userEmailSubscribed,
    submitting: false,
    showErrorsOnType: false
  });

  const locale = useLanguageRegionCode();

  useEffect(() => {
    if (enableRecaptcha) {
      loadRecaptcha((recaptchaObject: any) => {
        setRecaptchaObject(recaptchaObject);
        setDisableSubmit(false);
      });
    }
  }, [enableRecaptcha]);

  const [errors, setErrors] = useState<{ userRegEmail?: string; userRegPassword?: string; userRegFirstName?: string; userRegLastName?: string }>({});

  function showEmailSubscribed() {
    return countryId !== 313;
  }

  function handleSubscribedChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    if (!showEmailSubscribed()) {
      return;
    }

    const { userEmailSubscribed } = state;
    setState({ ...state, userEmailSubscribed: !userEmailSubscribed });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    e.preventDefault();
    const { name, value } = e.target;
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (state.showErrorsOnType) {
      setErrors(validateUserForm(name, value, errors).errors);
    }

    setState({ ...state, [name]: value });
  }

  function handleSuccessCallback(data: any) {
    setErrors({});

    setState({
      userRegEmail: '',
      userRegPassword: '',
      userRegFirstName: '',
      userRegLastName: '',
      userEmailSubscribed,
      submitting: false,
      showErrorsOnType: false
    });
    setServerError(null);
    setRecaptchaObject(null);
    setDisableSubmit(enableRecaptcha);

    console.log(data);

    if (handleRegistrationSuccess) {
      handleRegistrationSuccess(data);
    }
  }

  function handleErrorCallback(err: { responseJSON: { errors: any[] } }) {
    const serverResponseError = err && err.responseJSON ? err.responseJSON.errors[0] : { message: StringConstantsMixin.strings.ERR_TRY_AGAIN };

    if (serverResponseError && serverResponseError.target === 'email') {
      setErrors({ ...errors, userRegEmail: serverResponseError.message });
      setServerError(serverResponseError.message);
    } else if (serverResponseError && serverResponseError.message) {
      setServerError(serverResponseError.message);
    }
    setState({ ...state, submitting: false });

    track('Signup Clicked');
  }

  function submitRegisterHelper(recaptchaToken?: string) {
    emailSignupHandler(
      {
        first_name: state.userRegFirstName,
        last_name: state.userRegLastName,
        email: state.userRegEmail,
        password: state.userRegPassword,
        create_session: true,
        subscribed: state.userEmailSubscribed,
        recaptcha: recaptchaToken,
        referral_code: referralLink
      },
      locale,
      handleSuccessCallback,
      handleErrorCallback,
      (): void => null
    );
  }

  function submitRegister(e: React.KeyboardEvent<HTMLDivElement> | React.FormEvent) {
    e.preventDefault();
    if (disableSubmit || state.submitting) {
      return;
    }

    let validationErrors = {};
    validationErrors = validateUserForm('userRegFirstName', state.userRegFirstName, validationErrors).errors;
    validationErrors = validateUserForm('userRegLastName', state.userRegLastName, validationErrors).errors;
    validationErrors = validateUserForm('userRegEmail', state.userRegEmail, validationErrors).errors;
    validationErrors = validateUserForm('userRegPassword', state.userRegPassword, validationErrors).errors;

    if (Object.keys(validationErrors).length !== 0) {
      setErrors(validationErrors);
      setState({ ...state, showErrorsOnType: true });

      track('Signup Clicked');

      return;
    }

    setState({ ...state, submitting: true });
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (enableRecaptcha) {
      executeRecaptcha(recaptchaObject, 'userSignupLogin').then(
        (recaptchaToken: any) => submitRegisterHelper(recaptchaToken),
        () => setState({ ...state, submitting: false })
      );
    } else {
      submitRegisterHelper();
    }
  }

  const disabled = disableSubmit || state.submitting;

  return (
    <form id="signup-form" onSubmit={submitRegister}>
      <div className={formStyles.inputItem} data-testid="registrationForm-firstNameTextField">
        <OutlinedTextField
          autoFocus={autoFocus}
          name="userRegFirstName"
          errorMessage={errors.userRegFirstName}
          value={state.userRegFirstName}
          label={<FormattedMessage defaultMessage="First name" />}
          changeHandler={handleChange}
        />
      </div>
      <div className={formStyles.inputItem} data-testid="registrationForm-lastNameTextField">
        <OutlinedTextField
          name="userRegLastName"
          errorMessage={errors.userRegLastName}
          value={state.userRegLastName}
          label={<FormattedMessage defaultMessage="Last name" />}
          changeHandler={handleChange}
        />
      </div>
      <div className={`${formStyles.inputItem}`} data-testid="registrationForm-emailTextFeild">
        <OutlinedTextField
          name="userRegEmail"
          errorMessage={errors.userRegEmail}
          value={state.userRegEmail}
          label={<FormattedMessage defaultMessage="Email address" />}
          changeHandler={(e: React.ChangeEvent<HTMLInputElement>) => {
            setServerError(null);
            handleChange(e);
          }}
          type="email"
        />
      </div>
      <div
        className={`${formStyles.inputItem} ${showEmailSubscribed() ? styles.showSubscribedDistance : ''} `}
        data-testid="registrationForm-passwordTextFeild"
      >
        <OutlinedTextField
          name="userRegPassword"
          errorMessage={errors.userRegPassword}
          value={state.userRegPassword}
          label={<FormattedMessage defaultMessage="Password" />}
          changeHandler={handleChange}
          type="password"
        />
      </div>
      {showEmailSubscribed() && (
        <Checkbox
          handleChange={handleSubscribedChange}
          checked={state.userEmailSubscribed}
          label={<FormattedMessage defaultMessage="Product News, tips and special offers" />}
        />
      )}
      {serverError && <div className={`${formStyles.serverError} ${styles.center}`}>{serverError}</div>}
      <div className={styles.signup}>
        <Button
          text={signUp}
          className={styles.signup}
          disabled={disabled}
          onClick={() => {
            /* do nothing - form submit handles sign up */
          }}
          testId="register-form-sign-up"
          type="submit"
          variant="primary"
          width="full"
        />
      </div>
    </form>
  );
}
